import { defineStore } from "pinia"
import {
  CategoryOption, LevelOption,
  PLSession,
  SubjectSummary,
  SubjectOption, StatementSelectionsData, LevelGroup, SavingState, AlignedGoals
} from '@/presentlevels/presentlevels_types.ts'
import { PL_SUMMARY_SAVING_STATES } from '@/presentlevels/presentlevels_constants.ts'
import {
  getSubjectCategoriesWithSkills, getLevelOptions, getPresentlevelsSession,
  getSubjectOptions,
  postCategorySelections,
  getSubjectSummariesForSession, postPresentlevelsSession, postSubjectSummary, getSubjectSummaryByUUID, deleteSubjectSummary, putSubjectSummary, getReportUploadOptions, ReportTypeMetadata
} from "@/presentlevels/api_v2.ts";
import { getGoalByUUID } from "@/goals/api.ts";
import { Goal } from "@/goals/goal_types.ts";
import { deleteFolderResource } from '@/library/api'
import { HTTPError } from '@/util/http.ts'
import { Resource } from '@/resources/resource_types'
import { getResources } from '@/resources/api'
import { getAllAssessmentUUIDs } from "@/presentlevels/assessmentResourceUtil";
import { useVisibilityStore } from '@/visibility/visibility.store'
import { useLibraryStore } from '@/library/library.store.ts'

export const usePresentLevelsStore = defineStore('presentLevels', {
  state: () => ({
    plSession: null as PLSession | null,
    subjectSummaries: [] as SubjectSummary[],
    loadingSubjectSummaries: true,
    generatingStatements: {} as Record<string, boolean>,
    loadingAlignedGoals: {} as Record<string, boolean>,
    noNewAlignedGoals: {} as Record<string, boolean>,
    subjectOptions: [] as SubjectOption[],
    loadingSubjects: true,
    levelOptions: [] as LevelOption[],
    loadingLevels: true,
    categoryOptions: {} as { [subjectUUID: string]: CategoryOption[] },
    reportUploadOptions: {} as { [subjectLabel: string]: ReportTypeMetadata[] },
    reportTypeImages: {} as Record<string, string>,
    selectedSkillsMap: {
      strengths: {} as Record<string, boolean>,
      needs: {} as Record<string, boolean>,
    },
    resourcesByUUID: {} as Record<string, Resource>,
    errorDisplay: null as null | { displayHeader: string, displayBody: string },
    showAccessError: false as boolean,
    savingStates: {} as Record<string, SavingState>,
    savingStateTimeouts: {} as Record<string, ReturnType<typeof setTimeout>>,
  }),
  actions: {
    async fetchSessionDetails(sessionUUID: string, contentProfile?: string) {
      await Promise.all([
        this.fetchSession(sessionUUID),
        this.fetchSessionSummaries(sessionUUID)
          .then(() => {
            return Promise.all([
              this.fetchCategoriesForSummarySubjects(),
              useLibraryStore().fetchFoldersForPLSubjectSummaries(),
              this.fetchReportUploadOptions(contentProfile)
            ])
          }),
      ])
    },
    async fetchSession(sessionUUID: string) {
      try {
        this.plSession = await getPresentlevelsSession(sessionUUID)
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      }
    },
    async createSubjectSummaries(args: { studentName: string, gradeUUID: string, subjectUUIDs: string[], sessionUUID: string }) {
      for (const subjectUUID of args.subjectUUIDs) {
        try {
          this.subjectSummaries.push(await postSubjectSummary({
            student_name: args.studentName,
            subject_uuid: subjectUUID,
            subject_label: this.getSubjectLabelByUUID(subjectUUID),
            grade_uuid: args.gradeUUID,
            grade_label: this.getGradeLabelByUUID(args.gradeUUID),
            pl_session_uuid: args.sessionUUID
          }))
        } catch (e) {
          await this.setErrorDisplay(e)
        }
      }
    },
    async createSession(args: { studentName: string, gradeUUID: string, subjectUUIDs: string[] }) {
      try {
        this.plSession = await postPresentlevelsSession({
          student_name: args.studentName,
          enrolled_grade_uuid: args.gradeUUID,
          selected_subject_uuids: args.subjectUUIDs
        })
      } catch (e) {
        await this.setErrorDisplay(e)
        return
      }
      await this.createSubjectSummaries({
        studentName: args.studentName,
        gradeUUID: args.gradeUUID,
        subjectUUIDs: args.subjectUUIDs,
        sessionUUID: this.plSession.uuid
      })
    },
    async fetchSessionSummaries(sessionUUID: string) {
      try {
        this.loadingSubjectSummaries = true
        const summaries = await getSubjectSummariesForSession(sessionUUID)
        this.subjectSummaries = summaries
        this.loadingSubjectSummaries = false
        await this.syncResourcesWithCurrentSummaries()
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      }
    },

    async syncResourcesWithCurrentSummaries() {
      const statementTexts = this.subjectSummaries.map(s => s.statement_text)
      const currentUUIDs = getAllAssessmentUUIDs(statementTexts)

      const resourcesToKeep: Record<string, Resource> = {}
      const uuidsToFetch: string[] = []

      for (const uuid of currentUUIDs) {
        if (this.resourcesByUUID[uuid]) {
          resourcesToKeep[uuid] = this.resourcesByUUID[uuid]
        } else {
          uuidsToFetch.push(uuid)
        }
      }

      const updatedResourcesMap = { ...resourcesToKeep }

      if (uuidsToFetch.length > 0) {
        const newResources = await this.fetchResourcesForAssessmentUUIDs(uuidsToFetch)
        newResources.forEach(resource => {
          updatedResourcesMap[resource.uuid] = resource
        })
      }

      this.resourcesByUUID = updatedResourcesMap
    },

    async fetchSummaryByUUID(summaryUUID: string) {
      this.showAccessError = false
      try {
        this.loadingSubjectSummaries = true
        const summary = await getSubjectSummaryByUUID(summaryUUID)
        this.loadingSubjectSummaries = false
        this.subjectSummaries = [summary]
        await this.syncResourcesWithCurrentSummaries()
      } catch (e: any) {
        if (e?.cause?.status_code === 403 || e?.cause?.status_code === 404) {
          this.showAccessError = true
        } else {
          await this.setErrorDisplay(e)
          throw e
        }
      }
    },
    async fetchSubjectOptions() {
      const visibilityStore = useVisibilityStore()

      this.loadingSubjects = true
      try {
        this.subjectOptions = await getSubjectOptions()
        if (!visibilityStore.selVisibility) {
          // Replace "Social & Emotional Learning" with "Social Skills" when SEL visibility is disabled
          this.subjectOptions = this.subjectOptions.map(subject => {
            if (subject.label === 'Social & Emotional Learning') {
              return { ...subject, label: 'Social Skills' }
            }
            return subject
          })
        }
        this.loadingSubjects = false
      } catch (e) {
        await this.setErrorDisplay(e)
        this.loadingSubjects = false
        throw e
      }
    },
    async fetchLevelOptions() {
      this.loadingLevels = true
      try {
        this.levelOptions = await getLevelOptions()
        this.loadingLevels = false
      } catch (e) {
        await this.setErrorDisplay(e)
        this.loadingLevels = false
        throw e
      }
    },
    async fetchCategoriesForSummarySubjects() {
      const promises = []
      for (const subjectUUID of this.summarySubjectUUIDs) {
        promises.push(this.fetchCategoriesForSubject(subjectUUID))
      }
      try {
        await Promise.all(promises)
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      }
    },
    async fetchCategoriesForSubject(subjectUUID: string) {
      try {
        this.categoryOptions[subjectUUID] = await getSubjectCategoriesWithSkills(subjectUUID)
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      }
    },
    async fetchReportUploadOptions(contentProfile?: string) {
      try {
        const subjects = this.subjectSummaries.map(s => s.subject_label)
        if (subjects.length === 0) return
        
        const visibilityStore = useVisibilityStore()
        const response = await getReportUploadOptions({
          content_profile: contentProfile,
          subjects,
          beta: visibilityStore.betaVisibility
        })
        
        // Extract report_types array from each subject's response
        const extractedOptions: { [subjectLabel: string]: ReportTypeMetadata[] } = {}
        if (response) {
          for (const [subject, data] of Object.entries(response.options)) {
            extractedOptions[subject] = data.report_types || []
          }
          // Store the Base64 encoded images
          this.reportTypeImages = response.report_type_images || {}
        }
        
        this.reportUploadOptions = extractedOptions
      } catch (e) {
        console.error('Failed to fetch report upload options:', e)
        this.reportUploadOptions = {}
        this.reportTypeImages = {}
      }
    },
    async fetchResourcesForAssessmentUUIDs(uuids: string[]): Promise<Resource[]> {
      try {
        return await getResources(uuids)
      } catch (err) {
        console.error('Failed to fetch resources:', err)
        return []
      }
    },
    getSubjectLabelByUUID(subjectUUID: string) {
      const subject = this.subjectOptions.find(subject => subject.uuid === subjectUUID)
      return subject ? subject.label : ''
    },
    getGradeLabelByUUID(gradeUUID: string) {
      const grade = this.levelOptions.find(grade => grade.uuid === gradeUUID)
      return grade ? grade.label : ''
    },
    async setErrorDisplay(error: unknown) {
      const showGenericError = (!(error instanceof Error) || !error?.cause)
      if (showGenericError) {
        this.errorDisplay = {
          displayHeader: 'Error',
          displayBody: 'An error occurred'
        }
        return
      }

      this.errorDisplay = {
        displayHeader: error.message,
        displayBody: error.message
      }

      const httpErrorResponse = (error as HTTPError).response || (error.cause as HTTPError).response
      if (!httpErrorResponse) {
        return
      }

      let errorResponseMessage;
      try {
        const errorResponseJSON = await httpErrorResponse.json()
        errorResponseMessage = errorResponseJSON.error
      } catch {
        return
      }
      if (errorResponseMessage) {
        this.errorDisplay.displayBody += `: ${errorResponseMessage}`
      }
    },
    clearErrorDisplay() {
      this.errorDisplay = null
    },
    async submitStatementSelections(args: StatementSelectionsData) {
      this.generatingStatements[args.summaryUUID] = true
      this.clearNoNewAlignedGoalsFlag(args.summaryUUID)
      try {
        const updatedSummary = await postCategorySelections(args)
        const originalSummary = this.subjectSummaries.find(summary => summary.uuid === args.summaryUUID)
        // Remove the transient flag so it doesn't get stored
        const cleaned: SubjectSummary = {
          ...(updatedSummary as SubjectSummary),
          aligned_goals: originalSummary?.aligned_goals || updatedSummary.aligned_goals
        }
        
        // If there's an existing effect statement and the statement text changed, mark it as needing update
        if (originalSummary?.effect_statement && originalSummary.statement_text !== cleaned.statement_text) {
          cleaned.effect_statement_needs_update = true
        }

        await this.updateSummary(cleaned)
      } finally {
        this.generatingStatements[args.summaryUUID] = false
      }
    },
    async updateSubjectSummary(summaryUUID: string, fieldsToUpdate: Partial<SubjectSummary>) {
      try {
        const updatedSummary = await putSubjectSummary(summaryUUID, fieldsToUpdate)
        // Merge fieldsToUpdate with the response to ensure fields like effect_statement_needs_update
        // are preserved even if the backend doesn't return them yet
        // However, if the backend explicitly returns false for effect_statement_needs_update, respect that
        const mergedSummary = { ...updatedSummary, ...fieldsToUpdate }
        if (updatedSummary?.effect_statement_needs_update === false) {
          mergedSummary.effect_statement_needs_update = false
        }
        await this.updateSummary(mergedSummary)
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      }
    },
    clearSavingStateTimeout(summaryUUID: string) {
      if (this.savingStateTimeouts[summaryUUID]) {
        clearTimeout(this.savingStateTimeouts[summaryUUID])
        delete this.savingStateTimeouts[summaryUUID]
      }
    },
    setSavingState(summaryUUID: string, state: SavingState) {
      this.clearSavingStateTimeout(summaryUUID)
      this.savingStates[summaryUUID] = state

      if (state === PL_SUMMARY_SAVING_STATES.JUST_SAVED) {
        this.savingStateTimeouts[summaryUUID] = setTimeout(() => {
          this.savingStates[summaryUUID] = PL_SUMMARY_SAVING_STATES.NO_CHANGES
          delete this.savingStateTimeouts[summaryUUID]
        }, 3000)
      }
    },
    async saveSubjectSummary(
      summaryUUID: string,
      fieldsToUpdate: Partial<SubjectSummary>
    ): Promise<void> {
      if (!summaryUUID) return

      this.setSavingState(summaryUUID, PL_SUMMARY_SAVING_STATES.SAVING)
      try {
        await this.updateSubjectSummary(summaryUUID, fieldsToUpdate)
        this.setSavingState(summaryUUID, PL_SUMMARY_SAVING_STATES.JUST_SAVED)
      } catch (error) {
        this.setSavingState(summaryUUID, PL_SUMMARY_SAVING_STATES.UNSAVED_CHANGES)
        await this.setErrorDisplay(error)
        throw error
      }
    },
    async deleteStatement(summaryUUID: string) {
      const libraryStore = useLibraryStore()
      try {
        const libraryFolder = libraryStore.getFolderByContentUuid(summaryUUID)
        if (libraryFolder) {
          await deleteFolderResource(libraryFolder.uuid, summaryUUID, 'pl_student_summary')
        }
        await deleteSubjectSummary(summaryUUID)
        this.subjectSummaries = this.subjectSummaries.filter(summary => summary.uuid !== summaryUUID)
        delete this.savingStates[summaryUUID]

        this.clearSavingStateTimeout(summaryUUID)
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      }
    },
    async updateSummary(summary: SubjectSummary) {
      this.subjectSummaries = this.subjectSummaries.map((s) => {
        if (s.uuid === summary.uuid) {
          return summary
        }
        return s
      })

      await this.syncResourcesWithCurrentSummaries()
    },
    getSelectedStrengthUUIDs() {
      return this.getSelectedSkillUUIDsByType('strengths')
    },
    getSelectedNeedUUIDs() {
      return this.getSelectedSkillUUIDsByType('needs')
    },
    getSelectedSkillUUIDsByType(type: 'strengths' | 'needs'): string[] {
      const skillsByType = this.selectedSkillsMap[type]
      return Object.keys(skillsByType).filter((skillUUID) => skillsByType[skillUUID])
    },
    getCategoriesForSubject(subjectUUID: string): CategoryOption[] {
      return this.categoryOptions[subjectUUID] || []
    },
    resetSelectedSkills() {
      this.selectedSkillsMap = {
        strengths: {},
        needs: {}
      }
    },
    getLevelledSelectedSkillLabelsFromGroups(type: 'strengths' | 'needs', levelGroups: LevelGroup[]): Record<string, string[]> {
      const result: Record<string, string[]> = {}
      for (const levelGroup of levelGroups) {
        const selectedSkills = levelGroup.skills.filter((skill) => this.selectedSkillsMap[type][skill.uuid])
        if (selectedSkills.length) {
          result[levelGroup.label] = selectedSkills.map((skill) => skill.label)
        }
      }
      return result
    },
    async fetchAlignedGoalsForSubjectSummary(summaryUUID: string): Promise<AlignedGoals> {
      try {
        const summary = await getSubjectSummaryByUUID(summaryUUID)
        return summary.aligned_goals
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      }
    },
    async fetchGoalDetailsByUUID(goalUUIDs: string[]): Promise<Goal[]> {
      const goalPromises = goalUUIDs.map(async (goalUuid) => {
        try {
          return await getGoalByUUID(goalUuid)
        } catch (error) {
          // Silently skip invalid/deleted goals - don't raise error
          console.warn(`Skipping invalid goal ${goalUuid}`, error)
          return null
        }
      })
      const results = await Promise.all(goalPromises)
      // Filter out null values (invalid goals)
      return results.filter((goal): goal is Goal => goal !== null)
    },

    mergeAlignedGoalsWithDeduplication(existingAlignedGoals: AlignedGoals, newAlignedGoals: AlignedGoals): { mergedAlignedGoals: AlignedGoals, hasNewGoals: boolean } {
      const categoryGoalsMap = new Map<string, Goal[]>()

      const addGoalsToMap = (goals: Goal[], categoryLabel: string): boolean => {
        // Skip if goals is null or undefined
        if (!goals || goals.length === 0) {
          return false
        }
        
        if (categoryGoalsMap.has(categoryLabel)) {
          const existingGoals = categoryGoalsMap.get(categoryLabel)!
          const existingUUIDs = new Set(existingGoals.map(goal => goal.uuid))
          const newUniqueGoals = goals.filter(goal => !existingUUIDs.has(goal.uuid))
          categoryGoalsMap.get(categoryLabel)!.push(...newUniqueGoals)
          return newUniqueGoals.length > 0
        } else {
          // Remove duplicates within the goals array itself
          const uniqueGoals = goals.filter((goal, index, arr) =>
            arr.findIndex(g => g.uuid === goal.uuid) === index
          )
          categoryGoalsMap.set(categoryLabel, uniqueGoals)
          return uniqueGoals.length > 0
        }
      }

      for (const domainGoals of existingAlignedGoals) {
        for (const [categoryLabel, goals] of Object.entries(domainGoals)) {
          addGoalsToMap(goals, categoryLabel)
        }
      }

      let hasNewGoals = false
      for (const domainGoals of newAlignedGoals) {
        for (const [categoryLabel, goals] of Object.entries(domainGoals)) {
          if (addGoalsToMap(goals, categoryLabel)) {
            hasNewGoals = true
          }
        }
      }

      // Convert map back to AlignedGoals format
      const mergedAlignedGoals: AlignedGoals = []
      for (const [categoryLabel, goals] of categoryGoalsMap.entries()) {
        mergedAlignedGoals.push({ [categoryLabel]: goals })
      }

      return {
        mergedAlignedGoals,
        hasNewGoals
      }
    },
    async fetchAndUpdateAlignedGoalsForSummary(summaryUUID: string, alignedGoalsData: AlignedGoals) {
      this.loadingAlignedGoals[summaryUUID] = true
      try {
        // Check if alignedGoalsData has any goals - exit early if not
        const hasGoals = alignedGoalsData.some(domainGoals => {
          for (const goals of Object.values(domainGoals)) {
            if (goals && goals.length > 0) {
              return true
            }
          }
          return false
        })
        if (!hasGoals) {
          this.setNoNewAlignedGoals(summaryUUID, !hasGoals)
          return
        }

        const originalSummary = this.subjectSummaries.find(summary => summary.uuid === summaryUUID)
        if (!originalSummary) {
          return
        }

        const existingAlignedGoals = originalSummary.aligned_goals || []
        const { mergedAlignedGoals, hasNewGoals } = this.mergeAlignedGoalsWithDeduplication(existingAlignedGoals, alignedGoalsData)
        
        this.setNoNewAlignedGoals(summaryUUID, !hasNewGoals)
        
        const updatedSummary = {
          ...originalSummary,
          aligned_goals: mergedAlignedGoals
        }

        await this.updateSummary(updatedSummary)
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      } finally {
        this.loadingAlignedGoals[summaryUUID] = false
      }
    },
    setNoNewAlignedGoals(summaryUUID: string, noNewGoals: boolean) {
      this.noNewAlignedGoals[summaryUUID] = noNewGoals
    },
    clearNoNewAlignedGoalsFlag(summaryUUID: string) {
      this.noNewAlignedGoals[summaryUUID] = false
    }
  },
  getters: {
    summarySubjectUUIDs: (state) => {
      return Array.from(new Set(state.subjectSummaries.map((summary: SubjectSummary) => summary.subject_uuid)))
    },
    gradeOptions: (state) => {
      return state.levelOptions.filter((level: LevelOption) => level.level_type === 'academic')
    },
    enrolledGradeLabel: (state) => {
      if (!state.plSession) return ''

      const gradeOption = state.levelOptions.find(option => option.uuid === state.plSession?.enrolled_grade_uuid)
      return gradeOption?.label || ''
    },
    enrolledGrade: (state): LevelOption | null => {
      if (!state.plSession) return null

      return state.levelOptions.find(option => option.uuid === state.plSession?.enrolled_grade_uuid) ?? null
    },
    getAlignedGoalsForSummary: (state) => (summaryUUID: string) => {
      return state.subjectSummaries.find(summary => summary.uuid === summaryUUID)?.aligned_goals || []
    },
    getGradeLabelForSummary: (state) => (summaryUUID: string) => {
      return state.subjectSummaries.find(summary => summary.uuid === summaryUUID)?.grade_label || ''
    },
    isStatementGeneratingForSummaryUUID: (state) => (summaryUUID: string) => {
      return state.generatingStatements[summaryUUID] || false
    },
    isLoadingAlignedGoalsForSummaryUUID: (state) => (summaryUUID: string) => {
      return state.loadingAlignedGoals[summaryUUID] || false
    },
    noNewAlignedGoalsForSummaryUUID: (state) => (summaryUUID: string) => {
      return state.noNewAlignedGoals[summaryUUID] || false
    },
    visibleSubjectOptions: (state) => {
      const visibilityStore = useVisibilityStore()
      return state.subjectOptions.filter((subject: SubjectOption) =>
        !visibilityStore.hiddenPresentLevelsSubjectLabels.includes(subject.label)
      )
    }
  }
})